import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { handle401Redirect } from "./auth-utils";

const CSRF_COOKIE_NAME = "XSRF-TOKEN";
const CSRF_HEADER_NAME = "X-XSRF-TOKEN";

function getCsrfToken(): string | null {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === CSRF_COOKIE_NAME) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

function parseErrorMessage(text: string, statusText: string): string {
  // Try to parse as JSON and extract message
  try {
    const json = JSON.parse(text);
    if (json.message) {
      return json.message;
    }
    if (json.error) {
      return json.error;
    }
  } catch {
    // Not JSON, use text as-is
  }
  return text || statusText;
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    const message = parseErrorMessage(text, res.statusText);
    throw new Error(message);
  }
}

export async function apiRequest(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
  _csrfRetried?: boolean
) {
  const isFormData = body instanceof FormData;
  
  const headers: Record<string, string> = {};
  
  if (body && !isFormData) {
    headers["Content-Type"] = "application/json";
  }
  
  const csrfToken = getCsrfToken();
  if (csrfToken && method !== "GET") {
    headers[CSRF_HEADER_NAME] = csrfToken;
  }
  
  const res = await fetch(path, {
    method,
    headers: Object.keys(headers).length > 0 ? headers : undefined,
    credentials: "include",
    body: isFormData ? body : body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    if (res.status === 401) {
      queryClient.clear();
      if (handle401Redirect()) {
        return new Promise<never>(() => {});
      }
      // On login page — fall through to throw normally
    }

    const text = await res.text().catch(() => "");

    if (res.status === 403 && !_csrfRetried) {
      try {
        const json = JSON.parse(text);
        if (json.code === "CSRF_MISSING" || json.code === "CSRF_INVALID") {
          return apiRequest(method, path, body, true);
        }
      } catch {
        // Not JSON or no code field — treat as regular 403
      }
    }

    const message = parseErrorMessage(text, res.statusText);
    throw new Error(message);
  }
  const ctype = res.headers.get("content-type") || "";
  if (ctype.includes("application/json")) return res.json();
  return res.text();
}

export const api = {
  get: (p: string) => apiRequest("GET", p),
  post: (p: string, b?: unknown) => apiRequest("POST", p, b),
  put: (p: string, b?: unknown) => apiRequest("PUT", p, b),
  patch: (p: string, b?: unknown) => apiRequest("PATCH", p, b),
  del: (p: string, b?: unknown) => apiRequest("DELETE", p, b),
};

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (res.status === 401) {
      if (unauthorizedBehavior === "returnNull") {
        return null;
      }
      queryClient.clear();
      if (handle401Redirect()) {
        return new Promise<never>(() => {});
      }
      const text = (await res.text().catch(() => "")) || res.statusText;
      throw new Error(parseErrorMessage(text, res.statusText));
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: true,
      staleTime: 30_000,
      retry: 1,
    },
    mutations: {
      retry: false,
    },
  },
});
